import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import RewardHighlightCard from "../components/RewardHighlightCard";
import { api, parseErrorDetail } from "../lib/api";
import { useCart } from "../context/CartContext";

const CENTS = (c) => (c == null ? "—" : `$${(c / 100).toFixed(2)}`);
const PS_THRESHOLD = 20;

export default function PremiumSpecial() {
  const nav = useNavigate();
  const { cart, setQty, inc } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/catalogue");
        if (cancelled) return;
        setProducts(r.data.products.filter((p) => p.category === "PREMIUM_SPECIAL"));
      } catch (err) {
        if (cancelled) return;
        setError(parseErrorDetail(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const combined = useMemo(
    () => products.reduce((s, p) => s + (cart[p.product_id] || 0), 0),
    [cart, products]
  );
  const supportActive = combined >= PS_THRESHOLD;
  const potentialSavings = useMemo(() => {
    if (supportActive) return 0;
    return products.reduce((sum, p) => {
      const qty = cart[p.product_id] || 0;
      const diff = (p.prices_cents.standard || 0) - (p.prices_cents.mixed_twenty || 0);
      return sum + Math.max(diff, 0) * qty;
    }, 0);
  }, [cart, products, supportActive]);

  const subtotalEx = useMemo(() => products.reduce((sum, p) => {
    const qty = cart[p.product_id] || 0;
    const unit = supportActive ? (p.prices_cents.mixed_twenty ?? p.prices_cents.standard) : p.prices_cents.standard;
    return sum + qty * unit;
  }, 0), [cart, products, supportActive]);

  return (
    <PortalLayout title="Premium & Special Products Order">
      <div className="apwo-sticky-band" data-testid="ps-sticky-band">
        <div className={`apwo-ps-mini ${supportActive ? "apwo-ps-mini--on" : ""}`} data-testid="ps-threshold-banner">
          <div className="apwo-ps-mini-body">
            <span className="apwo-ps-mini-count" data-testid="ps-count">{combined}<span className="apwo-ps-mini-of">/{PS_THRESHOLD} units</span></span>
            <div className="apwo-ps-mini-bar" role="progressbar" aria-valuemin={0} aria-valuemax={PS_THRESHOLD} aria-valuenow={Math.min(combined, PS_THRESHOLD)}>
              <div className="apwo-ps-mini-fill" style={{ width: `${Math.min((combined / PS_THRESHOLD) * 100, 100)}%` }} />
            </div>
            <span className="apwo-ps-mini-msg">
              {supportActive
                ? "Support pricing applies to every Premium & Special line."
                : `Add ${PS_THRESHOLD - combined} more unit${PS_THRESHOLD - combined === 1 ? "" : "s"} to unlock support pricing.`}
            </span>
          </div>
        </div>
        <RewardHighlightCard />
      </div>

      <div className="apwo-panel apwo-info-panel" data-testid="ps-info">
        <span className="apwo-info-icon" aria-hidden>ⓘ</span>
        <div>
          <p className="apwo-info-title">Order any quantity of Premium &amp; Special products.</p>
          <p className="apwo-info-legend">
            Under {PS_THRESHOLD} total units: standard price. At {PS_THRESHOLD}+ total units across approved Premium &amp; Special products: support price.
          </p>
        </div>
      </div>

      {loading && <p className="apwo-muted" data-testid="ps-loading">Loading catalogue…</p>}
      {error && <div className="apwo-alert-error" data-testid="ps-error">{error}</div>}

      {!loading && !error && (
        <div className="apwo-panel apwo-products-panel" data-testid="ps-products">
          <table className="apwo-products-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Type</th>
                <th>Specifications</th>
                <th className="apwo-th-right">Standard<br/><span className="apwo-cell-muted">(&lt;{PS_THRESHOLD} units)</span></th>
                <th className="apwo-th-right">Mixed {PS_THRESHOLD}+ Support</th>
                <th className="apwo-th-center">Qty</th>
                <th className="apwo-th-right">Line total (ex GST)</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => {
                const qty = cart[p.product_id] || 0;
                const unit = supportActive ? (p.prices_cents.mixed_twenty ?? p.prices_cents.standard) : p.prices_cents.standard;
                const line = qty * unit;
                const perUnitSave = (p.prices_cents.standard || 0) - (p.prices_cents.mixed_twenty || 0);
                const outOfStock = p.in_stock === false;
                return (
                  <tr key={p.product_id}
                      className={outOfStock ? "apwo-oos-row" : ""}
                      data-testid={`ps-row-${p.product_id}`}>
                    <td>
                      <div className="apwo-ps-name">
                        {p.display_name}
                        {outOfStock && (
                          <span className="apwo-oos-badge"
                                data-testid={`ps-oos-${p.product_id}`}>Out of stock</span>
                        )}
                      </div>
                      <div className="apwo-cell-muted">Full Synthetic</div>
                    </td>
                    <td className="apwo-cell-muted">{p.type}</td>
                    <td className="apwo-cell-muted">{p.specifications}</td>
                    <td className="apwo-th-right">{CENTS(p.prices_cents.standard)}</td>
                    <td className="apwo-th-right apwo-price-support">
                      {CENTS(p.prices_cents.mixed_twenty)}
                      {perUnitSave > 0 && (
                        <div className="apwo-cell-muted apwo-nowrap">Save {CENTS(perUnitSave)}</div>
                      )}
                    </td>
                    <td className="apwo-th-center">
                      <div className="apwo-qty-inline">
                        <button className="apwo-qty-btn" onClick={() => inc(p.product_id, -1)} disabled={outOfStock} aria-label={`Decrease ${p.display_name}`} data-testid={`ps-dec-${p.product_id}`}>−</button>
                        <input
                          type="number" min="0" max="10000" step="1" inputMode="numeric"
                          value={outOfStock ? 0 : qty}
                          onChange={(e) => setQty(p.product_id, e.target.value)}
                          className="apwo-qty-input apwo-qty-input--sm"
                          disabled={outOfStock}
                          aria-label={`Quantity for ${p.display_name}`}
                          data-testid={`ps-qty-${p.product_id}`}
                        />
                        <button className="apwo-qty-btn" onClick={() => inc(p.product_id, +1)} disabled={outOfStock} aria-label={`Increase ${p.display_name}`} data-testid={`ps-inc-${p.product_id}`}>+</button>
                      </div>
                    </td>
                    <td className="apwo-th-right apwo-line-total">{CENTS(line)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="apwo-panel-footer">
            <div className={`apwo-savings-note ${supportActive ? "" : "apwo-savings-note--pending"}`} data-testid="ps-potential">
              <span className="apwo-savings-title">
                {supportActive ? "Support pricing savings" : `Potential savings at ${PS_THRESHOLD}+ units`}
              </span>
              <span className="apwo-savings-value">{CENTS(supportActive ? 0 : potentialSavings)}</span>
            </div>
            <div className="apwo-subtotal">
              <span className="apwo-sub-label">TOTAL PREMIUM &amp; SPECIAL (ex GST)</span>
              <span className="apwo-sub-value" data-testid="ps-subtotal">{CENTS(subtotalEx)}</span>
            </div>
            <button
              type="button"
              className="apwo-btn-primary apwo-btn-primary--nav"
              onClick={() => nav("/portal/summary")}
              data-testid="ps-continue"
            >Continue to Order Summary</button>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
